
import {zip as observableZip,  Observable } from 'rxjs';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { CategoryService } from '../../services/category.service';
import { LocationService } from '../../services/location.service';
import { SelectableCategory } from '../../models/categories/category.models';
import { LocationsByRegionDto, SelectableRegion, SelectableLocation } from '../../models/locations/location.models';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { AccountService } from '../../services/account.service';
import { MatchPreferencesDto, UpdateMatchPreferencesDto } from '../../models/account/account.models';
import { CategoryAndLocationHelpers } from '../../helpers';
import { LoggedInUserService } from '../../auth/logged.in.user';
import { CmsDialogService } from '../../services/cms.dialog.service';
import { CmsDialogDto } from '../../models/cms.dialogs/cms.dialogs.models';

@Component({
    selector: 'tender-alert-profile',
    template: require('./tender.alert.profile.component.html'),
    styles: [require('./personal.account.shared.css'), require('./tender.alert.profile.component.css')]
})
export class TenderAlertProfileComponent implements OnInit, OnDestroy {
    categories: Array<SelectableCategory>;
    locationsByRegion: LocationsByRegionDto;
    matchPreferences: MatchPreferencesDto;

    showCatLocError = false;

    @BlockUI('categories-and-locations-block') private catAndLocBlock : NgBlockUI;

    categoryFilter: string;

    tooManyCategoriesSelected = false;

    dialogHeader: string;
    dialogBody: string;

    subscribed: boolean;

    constructor(private readonly categoryService: CategoryService,
        private readonly locationService: LocationService,
        private readonly accountService: AccountService,
        private readonly helpers: CategoryAndLocationHelpers,
        loggedInUserService: LoggedInUserService,
        private readonly cmsDialogService: CmsDialogService) {

        localStorage.setItem('tooManyCategoriesSelected', 'false');
        localStorage.setItem('categoriesSelected', '0');
        localStorage.setItem('maxCategories', '29');

        var user = loggedInUserService.getCurrentUser();
        this.subscribed = !user.canAdHoc('notSubscribed');

        if (this.subscribed) {
            localStorage.setItem('maxCategories', '1000');
        }
    }

    ngOnInit() {
        document.body.classList.add('personal-account-fit');

        this.cmsDialogService.getByDescription('TooManyCategories').subscribe((result: CmsDialogDto) => {
            this.dialogHeader = result.header;
            this.dialogBody = result.body;
            localStorage.setItem('categoriesDialogHeader', this.dialogHeader);
            localStorage.setItem('categoriesDialogBody', this.dialogBody);
        });

        this.loadMatchPreferences();
    }

    ngOnDestroy() {
        document.body.classList.remove('personal-account-fit');
    }

    private loadMatchPreferences() {
        var categoryObservable = this.categoryService.getActiveCategories();
        var locationObservable = this.locationService.getActiveLocations();
        var matchPreferencesObservable = this.accountService.getMatchPreferences();

        this.catAndLocBlock.start();

        observableZip(categoryObservable,
            locationObservable,
            matchPreferencesObservable,
            (categories: Array<SelectableCategory>,
                locations: LocationsByRegionDto,
                preferences: MatchPreferencesDto) => ({ categories, locations, preferences }))
            .subscribe(results => {
                this.categories = results.categories;
                this.locationsByRegion = results.locations;
                this.matchPreferences = results.preferences;

                this.helpers.setSelectedCategories(this.categories, this.matchPreferences.categoryIds);
                this.helpers.setSelectedLocations(this.locationsByRegion, this.matchPreferences.locationIds);

                this.catAndLocBlock.stop();

                var categoriesSelectedCount = 0;
                this.categories.forEach((category: SelectableCategory) => {
                    if (category.children.length == 0) {
                        if (category.isSelected) {
                            categoriesSelectedCount++;
                        }
                    }
                    else {
                        category.children.forEach((child: SelectableCategory) => {
                            if (child.isSelected) {
                                categoriesSelectedCount++;
                            }
                        });
                    }
                });

                localStorage.setItem('categoriesSelected', categoriesSelectedCount.toString());

                if (categoriesSelectedCount == 29 && !this.subscribed) {
                    this.tooManyCategoriesSelected = false;
                    localStorage.setItem('tooManyCategoriesSelected', 'true');
                }
                else if (categoriesSelectedCount > 29 && !this.subscribed) {
                    this.tooManyCategoriesSelected = true;
                    localStorage.setItem('tooManyCategoriesSelected', 'true');
                }
                else {
                    this.tooManyCategoriesSelected = false;
                    localStorage.setItem('tooManyCategoriesSelected', 'false');
                }

            });
    }

    async filterCategories() {
        if (this.categoryFilter && this.categoryFilter.length >= 3) {
            var filter = this.categoryFilter.toLowerCase();
            this.categories.forEach(async (category: SelectableCategory) => {
                await this.checkIfCategoryOrChildrenMatch(category, filter);
            });
        } else {
            this.categories.forEach((category: SelectableCategory) => { this.showCategoryAndChildren(category) });
        }
    }

    async checkIfCategoryOrChildrenMatch(category: SelectableCategory, filter: string, parent?: SelectableCategory) {
        category.show = category.name.toLowerCase().indexOf(filter) > -1 ||
            (category.keywords && category.keywords.indexOf(filter) > -1);

        if (parent && category.show) {
            parent.show = true;
            parent.isExpanded = true;
        }

        category.children.forEach(async (child: SelectableCategory) => {
            await this.checkIfCategoryOrChildrenMatch(child, filter, category);
        });
    }

    async showCategoryAndChildren(category: SelectableCategory) {
        category.show = true;

        category.children.forEach((child: SelectableCategory) => { this.showCategoryAndChildren(child) });
    }

    changeRegionSelected(region: SelectableRegion) {
        region.locations.forEach((location: SelectableLocation) => {
            location.isSelected = region.isSelected;
        });
    }

    changeLocationSelected(location: SelectableLocation, region?: SelectableRegion) {
        if (!region) {
            return;
        }

        if (region.locations.every((l: SelectableLocation) => { return l.isSelected })) {
            region.isSelected = true;
        } else if (region.locations.every((l: SelectableLocation) => { return !l.isSelected })) {
            region.isSelected = false;
        }
    }

    categoriesChanged() {
        var categoriesSelectedCount = 0;
        this.categories.forEach((category: SelectableCategory) => {
            if (category.children.length == 0) {
                if (category.isSelected) {
                    categoriesSelectedCount++;
                }
            }
            else {
                category.children.forEach((child: SelectableCategory) => {
                    if (child.isSelected) {
                        categoriesSelectedCount++;
                    }
                });
            }
        });

        localStorage.setItem('categoriesSelected', categoriesSelectedCount.toString());

        if (categoriesSelectedCount == 29 && !this.subscribed) {
            this.tooManyCategoriesSelected = false;
            localStorage.setItem('tooManyCategoriesSelected', 'true');
        }
        else if (categoriesSelectedCount > 29 && !this.subscribed) {
            this.tooManyCategoriesSelected = true;
            localStorage.setItem('tooManyCategoriesSelected', 'true');
        }
        else {
            this.tooManyCategoriesSelected = false;
            localStorage.setItem('tooManyCategoriesSelected', 'false');
        }
    }

    saveMatchPreferences() {
        this.catAndLocBlock.start();

        this.tooManyCategoriesSelected = false;

        var dto: UpdateMatchPreferencesDto = {
            categoryIds: [],
            locationIds: []
        };

        var checkCategorySelection = (category: SelectableCategory) => {
            if (category.isSelected) {
                dto.categoryIds.push(category.id);
            }

            category.children.forEach((child: SelectableCategory) => {
                checkCategorySelection(child);
            });
        }

        this.categories.forEach((category: SelectableCategory) => { checkCategorySelection(category); });

        var checkLocationSelection = (location: SelectableLocation) => {
            if (location.isSelected) {
                dto.locationIds.push(location.id);
            }
        }

        this.locationsByRegion.regions.forEach((r: SelectableRegion) => {
            r.locations.forEach((l: SelectableLocation) => { checkLocationSelection(l); });
        });

        this.locationsByRegion.locationsWithoutRegion.forEach((l: SelectableLocation) => {
            checkLocationSelection(l);
        });

        if (dto.categoryIds.length === 0 || dto.locationIds.length === 0) {
            this.showCatLocError = true;
            this.catAndLocBlock.stop();
            return;
        }

        var categoriesSelectedCount = 0;
        this.categories.forEach((category: SelectableCategory) => {
            if (category.children.length == 0) {
                if (category.isSelected) {
                    categoriesSelectedCount++;
                }
            }
            else {
                category.children.forEach((child: SelectableCategory) => {
                    if (child.isSelected) {
                        categoriesSelectedCount++;
                    }
                });
            }
        });

        if (categoriesSelectedCount > 29 && !this.subscribed) {
            this.tooManyCategoriesSelected = true;
            this.catAndLocBlock.stop();
            return;
        }

        this.showCatLocError = false;

        this.accountService.updateMatchPreferences(dto).subscribe(() => {
            this.catAndLocBlock.stop();
        }, () => {
            this.catAndLocBlock.stop();
        });
    }
}


// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./Client/app/views/account.management/tender.alert.profile.component.ts